import { supabase } from '../lib/supabase';
import { sendShopOtpEmail } from './shopEmailService';

interface SyncData {
  customerProfileId?: number;
  email: string;
  deviceId: string;
  cartItems?: Array<{
    productId: number;
    quantity: number;
    price: number;
    title: string;
    imageUrl?: string;
  }>;
  lastSynced?: string;
}

interface SyncResult {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
}

/**
 * Service pour la synchronisation des données entre appareils
 * Permet de synchroniser le panier et les préférences utilisateur entre différents appareils
 * via email et OTP
 */
const deviceSyncService = {
  /**
   * Génère un identifiant unique pour l'appareil actuel
   */
  generateDeviceId: (): string => {
    // Vérifier si un ID existe déjà
    let deviceId = localStorage.getItem('device_id');
    
    // Si non, en générer un nouveau
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('device_id', deviceId);
    }
    
    return deviceId;
  },
  
  /**
   * Enregistre les données de synchronisation pour un appareil
   */
  registerDevice: async (email: string): Promise<SyncResult> => {
    try {
      const deviceId = deviceSyncService.generateDeviceId();
      
      // Vérifier si le profil client existe
      const { data: profile, error: profileError } = await supabase
        .from('shop_customer_profiles')
        .select('id')
        .eq('email', email)
        .single();
      
      if (profileError && profileError.code !== 'PGRST116') {
        throw new Error(`Erreur lors de la recherche du profil: ${profileError.message}`);
      }
      
      // Créer ou mettre à jour l'entrée de synchronisation
      const { data, error } = await supabase
        .from('shop_device_sync')
        .upsert({
          email,
          device_id: deviceId,
          customer_profile_id: profile?.id,
          cart_items: JSON.parse(localStorage.getItem('cart') || '[]'),
          last_synced: new Date().toISOString()
        }, {
          onConflict: 'email,device_id'
        });
      
      if (error) throw error;
      
      return {
        success: true,
        message: 'Appareil enregistré avec succès',
        data
      };
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'appareil:', error);
      return {
        success: false,
        message: 'Échec de l\'enregistrement de l\'appareil',
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  },
  
  /**
   * Envoie un code OTP pour la synchronisation entre appareils
   */
  sendSyncOTP: async (email: string): Promise<SyncResult> => {
    try {
      // Générer un code OTP à 6 chiffres
      const otpCode = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Enregistrer le code OTP dans la base de données
      const { error } = await supabase
        .from('shop_customer_profiles')
        .update({
          otp_code: otpCode,
          otp_expiry: new Date(Date.now() + 10 * 60 * 1000).toISOString() // 10 minutes
        })
        .eq('email', email);
      
      if (error) throw error;
      
      // Envoyer l'email avec le code OTP
      const emailResult = await sendShopOtpEmail({
        to: email,
        name: email.split('@')[0],
        otpCode,
        purpose: 'sync_devices' as any
      });
      
      if (!emailResult.success) {
        throw new Error(`Erreur lors de l'envoi de l'email: ${emailResult.error}`);
      }
      
      return {
        success: true,
        message: 'Code OTP envoyé avec succès'
      };
    } catch (error) {
      console.error('Erreur lors de l\'envoi du code OTP:', error);
      return {
        success: false,
        message: 'Échec de l\'envoi du code OTP',
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  },
  
  /**
   * Vérifie le code OTP et synchronise les données entre appareils
   */
  verifySyncOTP: async (email: string, otpCode: string): Promise<SyncResult> => {
    try {
      // Vérifier le code OTP
      const { data: profile, error: profileError } = await supabase
        .from('shop_customer_profiles')
        .select('id, otp_code, otp_expiry')
        .eq('email', email)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profile) {
        return {
          success: false,
          message: 'Profil non trouvé',
          error: 'Aucun profil associé à cet email'
        };
      }
      
      // Vérifier si le code OTP est valide et non expiré
      if (profile.otp_code !== otpCode) {
        return {
          success: false,
          message: 'Code OTP invalide',
          error: 'Le code OTP fourni est incorrect'
        };
      }
      
      if (new Date(profile.otp_expiry) < new Date()) {
        return {
          success: false,
          message: 'Code OTP expiré',
          error: 'Le code OTP a expiré, veuillez en demander un nouveau'
        };
      }
      
      // Récupérer les données de synchronisation
      const { data: syncData, error: syncError } = await supabase
        .from('shop_device_sync')
        .select('*')
        .eq('customer_profile_id', profile.id)
        .order('last_synced', { ascending: false })
        .limit(1);
      
      if (syncError) throw syncError;
      
      // Enregistrer l'appareil actuel
      const deviceId = deviceSyncService.generateDeviceId();
      
      // Fusionner les données du panier
      let cartItems = JSON.parse(localStorage.getItem('cart') || '[]');
      
      if (syncData && syncData.length > 0 && syncData[0].cart_items) {
        // Fusionner les paniers en évitant les doublons
        const remoteCart = syncData[0].cart_items;
        
        // Créer un map pour faciliter la fusion
        const cartMap = new Map();
        
        // Ajouter les articles du panier local
        cartItems.forEach((item: any) => {
          cartMap.set(item.productId, item);
        });
        
        // Ajouter ou mettre à jour avec les articles du panier distant
        remoteCart.forEach((item: any) => {
          if (cartMap.has(item.productId)) {
            // Prendre la quantité la plus récente
            const localItem = cartMap.get(item.productId);
            cartMap.set(item.productId, {
              ...item,
              quantity: Math.max(localItem.quantity, item.quantity)
            });
          } else {
            cartMap.set(item.productId, item);
          }
        });
        
        // Convertir le map en tableau
        cartItems = Array.from(cartMap.values());
        
        // Mettre à jour le panier local
        localStorage.setItem('cart', JSON.stringify(cartItems));
      }
      
      // Mettre à jour les données de synchronisation
      await supabase
        .from('shop_device_sync')
        .upsert({
          email,
          device_id: deviceId,
          customer_profile_id: profile.id,
          cart_items: cartItems,
          last_synced: new Date().toISOString()
        }, {
          onConflict: 'email,device_id'
        });
      
      // Réinitialiser le code OTP
      await supabase
        .from('shop_customer_profiles')
        .update({
          otp_code: null,
          otp_expiry: null
        })
        .eq('id', profile.id);
      
      return {
        success: true,
        message: 'Synchronisation réussie',
        data: { cartItems }
      };
    } catch (error) {
      console.error('Erreur lors de la vérification du code OTP:', error);
      return {
        success: false,
        message: 'Échec de la synchronisation',
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  },
  
  /**
   * Synchronise le panier local avec le serveur
   */
  syncCart: async (): Promise<SyncResult> => {
    try {
      const token = localStorage.getItem('customer_access_token');
      const deviceId = deviceSyncService.generateDeviceId();
      
      if (!token) {
        return {
          success: false,
          message: 'Non authentifié',
          error: 'Vous devez être connecté pour synchroniser votre panier'
        };
      }
      
      // Récupérer le profil client
      const { data: profile, error: profileError } = await supabase
        .from('shop_customer_profiles')
        .select('id, email')
        .eq('access_token', token)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profile) {
        return {
          success: false,
          message: 'Profil non trouvé',
          error: 'Aucun profil associé à ce token'
        };
      }
      
      // Mettre à jour les données de synchronisation
      const cartItems = JSON.parse(localStorage.getItem('cart') || '[]');
      
      const { error } = await supabase
        .from('shop_device_sync')
        .upsert({
          email: profile.email,
          device_id: deviceId,
          customer_profile_id: profile.id,
          cart_items: cartItems,
          last_synced: new Date().toISOString()
        }, {
          onConflict: 'email,device_id'
        });
      
      if (error) throw error;
      
      return {
        success: true,
        message: 'Panier synchronisé avec succès'
      };
    } catch (error) {
      console.error('Erreur lors de la synchronisation du panier:', error);
      return {
        success: false,
        message: 'Échec de la synchronisation du panier',
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }
};

export default deviceSyncService;
